import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils.ts'

interface Breadcrumb {
  label: ReactNode
  to?: string
  /** Alias for to */
  href?: string
}

interface PageHeaderProps {
  title: ReactNode
  description?: ReactNode
  /** Optional icon displayed next to the title */
  icon?: ReactNode
  breadcrumbs?: Breadcrumb[]
  actions?: ReactNode
  className?: string
}

/**
 * Standard-Seitenkopf: Breadcrumbs + Titel + Beschreibung + Actions-Slot.
 * Ersetzt uneinheitliche `<h1>`-Bloecke in den Pages.
 */
export function PageHeader({
  title,
  description,
  icon,
  breadcrumbs,
  actions,
  className,
}: PageHeaderProps) {
  return (
    <div className={cn('mb-4 flex flex-col gap-2', className)}>
      {breadcrumbs && breadcrumbs.length > 0 ? (
        <nav className="flex items-center gap-1 text-caption text-gray-500">
          {breadcrumbs.map((crumb, i) => (
            <span key={i} className="flex items-center gap-1">
              {i > 0 ? <ChevronRight className="h-3 w-3 text-gray-400" /> : null}
              {(crumb.to ?? crumb.href) ? (
                <Link to={(crumb.to ?? crumb.href)!} className="hover:text-gray-700 transition">
                  {crumb.label}
                </Link>
              ) : (
                <span>{crumb.label}</span>
              )}
            </span>
          ))}
        </nav>
      ) : null}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex flex-col gap-1 min-w-0">
          <h1 className="text-h2 text-gray-900 flex items-center gap-2">
            {icon ? <span className="shrink-0">{icon}</span> : null}
            {title}
          </h1>
          {description ? (
            <p className="text-small text-gray-600 max-w-2xl">{description}</p>
          ) : null}
        </div>
        {actions ? <div className="flex items-center gap-2 shrink-0">{actions}</div> : null}
      </div>
    </div>
  )
}
